/*
 * <product-slider-dots> — dot pagination for any Dawn slider-component track.
 *
 * SliderComponent (unlike SlideshowComponent) has no dot support, so this
 * drives the track directly and only reads public DOM — no Dawn internals.
 *
 * A page is a full screenful of slides, not one slide: 4 cards at 2 visible
 * gives 2 dots, not 3. Dawn's own arrows step one card at a time
 * (totalPages = cards - visible + 1); that model is deliberately not used
 * here. How many slides are visible depends on the viewport, which is why
 * the buttons are built at runtime rather than in Liquid.
 *
 * Shared by featured-collection (product cards, several per view) and the
 * product media gallery (one image per view). It lives in assets/ rather
 * than in a section's {% javascript %} block because two sections need it
 * and duplicated copies would drift.
 *
 * Attributes:
 *   data-track            id of the scrolling <ul> ([id^="Slider-"])
 *   data-per-view-mobile  slides visible per page below 750px
 *   data-per-view-desktop slides visible per page at 750px and up
 *   data-label / data-of  translated strings for the aria-label
 *
 * Renders Dawn's own .slider-counter__link--dots markup, so Dawn's stock
 * dot styling in component-slider.css applies with no extra CSS.
 *
 * It hides itself whenever everything already fits on one page, which is
 * also what happens on any layout where the track is not a horizontal
 * scroller (stacked slides report no offset step, so metrics() bails).
 */
(function () {
  class ProductSliderDots extends HTMLElement {
    connectedCallback() {
      this.track = document.getElementById(this.dataset.track);
      if (!this.track) return;

      this.onScroll = () => this.sync();
      this.track.addEventListener('scroll', this.onScroll, { passive: true });

      // The slide count per view changes with width, so rebuild on resize.
      this.observer = new ResizeObserver(() => this.build());
      this.observer.observe(this.track);

      this.build();
    }

    disconnectedCallback() {
      if (this.track && this.onScroll) this.track.removeEventListener('scroll', this.onScroll);
      if (this.observer) this.observer.disconnect();
    }

    metrics() {
      // Hidden slides report zero width and must not count, same as Dawn.
      const slides = Array.from(this.track.querySelectorAll('[id^="Slide-"]')).filter((el) => el.clientWidth > 0);
      if (slides.length < 2) return null;

      const step = slides[1].offsetLeft - slides[0].offsetLeft;
      if (step <= 0) return null;

      // Slides per view comes from the caller's own settings, not from
      // measuring the track. Measuring looked reasonable but is unreliable
      // inside the theme editor's iframe, where a mid-resize reading can be
      // off by a card and turn 2 dots into 4. The settings are exact and are
      // what decides the column count in the first place.
      const isPhone = window.matchMedia('(max-width: 749px)').matches;
      const configured = parseInt(isPhone ? this.dataset.perViewMobile : this.dataset.perViewDesktop, 10);
      const perView = Math.max(1, configured || Math.round((this.track.clientWidth + step) / step) - 1);

      return {
        step: step,
        perView: perView,
        pageWidth: perView * step,
        pages: Math.max(1, Math.ceil(slides.length / perView)),
      };
    }

    build() {
      const m = this.metrics();

      // One page means everything already fits — no control needed.
      if (!m || m.pages < 2) {
        this.replaceChildren();
        this.dots = null;
        this.hidden = true;
        return;
      }

      this.hidden = false;
      if (this.dots && this.dots.length === m.pages) {
        this.sync();
        return;
      }

      const frag = document.createDocumentFragment();
      for (let i = 0; i < m.pages; i++) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'slider-counter__link slider-counter__link--dots link';
        button.setAttribute('aria-controls', this.track.id);
        button.setAttribute('aria-label', `${this.dataset.label} ${i + 1} ${this.dataset.of} ${m.pages}`);
        button.innerHTML = '<span class="dot"></span>';
        button.addEventListener('click', () => {
          const current = this.metrics();
          if (current) this.track.scrollTo({ left: i * current.pageWidth, behavior: 'smooth' });
        });
        frag.appendChild(button);
      }

      this.replaceChildren(frag);
      this.dots = Array.from(this.children);
      this.sync();
    }

    sync() {
      const m = this.metrics();
      if (!m || !this.dots) return;
      const index = Math.min(m.pages - 1, Math.max(0, Math.round(this.track.scrollLeft / m.pageWidth)));
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('slider-counter__link--active', i === index);
        dot.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
    }
  }

  if (!customElements.get('product-slider-dots')) {
    customElements.define('product-slider-dots', ProductSliderDots);
  }
})();
